// PaymentReport.js

import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import { useNavigate } from 'react-router-dom';
import moment from "moment";
import { cqAPI } from "../../lib/Auth";
import "react-datepicker/dist/react-datepicker.css";
import "./PaymentReport.css";
import { useReactToPrint } from 'react-to-print';

function InstallmentReport() {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const theme = useTheme();
  const { colorMode } = useColorMode();

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const dateFields = ["startDate", "lastPayment"];
  const numericFields = ["total", "paymentAmount", "downpayment", "remaining"];

  const sortedTransactions = useMemo(() => {
    let sortableItems = [...payments];
    if (sortConfig !== null && sortConfig.key) {
      sortableItems.sort((a, b) => {
        let va = a[sortConfig.key];
        let vb = b[sortConfig.key];

        if (dateFields.includes(sortConfig.key)) {
          va = new Date(va);
          vb = new Date(vb);
        } else if (numericFields.includes(sortConfig.key)) {
          va = parseFloat(va.replace(/[^0-9.-]+/g, ""));
          vb = parseFloat(vb.replace(/[^0-9.-]+/g, ""));
        } else {
          va = va.toString().toLowerCase();
          vb = vb.toString().toLowerCase();
        }

        if (va < vb) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (va > vb) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [payments, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const fetchPayments = async () => {
    setIsLoading(true);

    try {
      const response = await cqAPI.get(`/reports/installments`);

      if (response.type === "ok") {
        const transformData = (data) => {
          const fieldNames = [
            "total",
            "startDate",
            "status",
            "paymentAmount",
            "patient",
            "interval",
            "downpayment",
            "remaining",
            "lastPayment",
            "depositedTo",
          ];
          return data.map((entry) => {
            let obj = {};
            fieldNames.forEach((field, index) => {
              obj[field] = entry[index];
            });
            return obj;
          });
        };

        setPayments(transformData(response.data));
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
        position="relative"
      >
        <Spinner className="centered-spinner" color="blue.500" size="xl" />
      </Flex>
    );
  }

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  const totalSum = payments.reduce((acc, cur) => {
    const total = parseFloat(cur.total.replace(/[^0-9.-]+/g, ""));
    return acc + total;
  }, 0);

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <div ref={componentRef}>
        <VStack pacing={0} align="stretch">
          <Flex borderBottom="1px" borderColor="gray.200" p={2}>
            <HStack spacing={2} flex="1">
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("total")}
                cursor="pointer"
              >
                Total
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("patient")}
                cursor="pointer"
              >
                Patient
              </Text>
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("startDate")}
                cursor="pointer"
              >
                Start Date
              </Text>
              <Text
                fontSize="sm"
                flexBasis="8%"
                fontWeight="bold"
                onClick={() => requestSort("status")}
                cursor="pointer"
              >
                Status
              </Text>
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("paymentAmount")}
                cursor="pointer"
              >
                Payment Amount
              </Text>
              <Text
                fontSize="sm"
                flexBasis="7%"
                fontWeight="bold"
                onClick={() => requestSort("interval")}
                cursor="pointer"
              >
                Interval
              </Text>
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("downpayment")}
                cursor="pointer"
              >
                Downpayment
              </Text>
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("remaining")}
                cursor="pointer"
              >
                Remaining
              </Text>
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("lastPayment")}
                cursor="pointer"
              >
                Last Payment
              </Text>
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("depositedTo")}
                cursor="pointer"
              >
                Deposited To
              </Text>
            </HStack>
          </Flex>
          {sortedTransactions.map((payment, index) => (
            <Flex key={index} p={2}>
              <HStack spacing={2} flex="1">
                <Text fontSize="sm" flexBasis="10%">
                  {payment.total}
                </Text>
                <Text fontSize="sm" flexBasis="15%">
                  {payment.patient}
                </Text>
                <Text fontSize="sm" flexBasis="10%">
                  {moment(payment.startDate, "MM/DD/YYYY").format("MMM D, YYYY")}
                </Text>
                <Text fontSize="sm" flexBasis="8%">
                  {payment.status}
                </Text>
                <Text fontSize="sm" flexBasis="10%">
                  {payment.paymentAmount}
                </Text>
                <Text fontSize="sm" flexBasis="7%">
                  {payment.interval}
                </Text>
                <Text fontSize="sm" flexBasis="10%">
                  {payment.downpayment}
                </Text>
                <Text fontSize="sm" flexBasis="10%">
                  {payment.remaining}
                </Text>
                <Text fontSize="sm" flexBasis="10%">
                  {moment(payment.lastPayment, "MM/DD/YYYY").format("MMM D, YYYY")}
                </Text>
                <Text fontSize="sm" flexBasis="10%">
                  {payment.depositedTo}
                </Text>
              </HStack>
            </Flex>
          ))}
        </VStack>
        <Box textAlign="right" mt={4}>
          <Text fontWeight="bold">
            Total Sum: ${totalSum.toFixed(2)}
          </Text>
        </Box>
      </div>
    </Box>
  );
}

export default InstallmentReport;
